import {
  useLocalParticipant,
  useMediaDevices,
  usePersistentUserChoices,
  useRoomContext,
} from "@livekit/components-react"
import {
  Box,
  BoxProps,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  ChatCircleText,
  Copy,
  Gear,
  IconProps,
  Microphone,
  MicrophoneSlash,
  PhoneSlash,
  VideoCamera,
  X,
} from "@phosphor-icons/react"
import { VideoCameraSlash } from "@phosphor-icons/react/dist/ssr"
import { Track } from "livekit-client"
import React, { useState } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { Colors } from "../../constants/colors"
import { StateAction } from "../../types/State"

const containerProps: BoxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  sx: {
    cursor: "pointer",
    backgroundColor: "rgba(36, 36, 36, 0.6)",
    "&:hover": {
      backgroundColor: "rgba(36, 36, 36, 0.8)",
    },
  },
  width: 45,
  height: 45,
  borderRadius: "50%",
}

const iconProps: IconProps = {
  size: 32,
}

interface CustomControlsBarProps {
  showChat: boolean
  toggleChat: () => void
  disconnect: () => void
  audioState: boolean
  videoState: boolean
}

interface DeviceOption {
  label: string
  deviceId: string
}

const DeviceSelectionDialog = ({
  open,
  onClose,
  audioInputs,
  videoInputs,
  selectedAudioInput,
  selectedVideoInput,
  handleAudioInputChange,
  handleVideoInputChange,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(8px)",
        borderRadius: "24px",
        minWidth: { xs: "90vw", sm: "400px" },
        maxWidth: "500px",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      },
    }}
  >
    <DialogTitle
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: Colors.White,
        pb: 1,
      }}
    >
      Device Settings
      <IconButton
        onClick={onClose}
        sx={{
          color: Colors.White,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <X size={24} />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ color: Colors.White, mb: 1, fontWeight: 500 }}
        >
          Microphone
        </Typography>
        <FormControl fullWidth>
          <Select
            value={selectedAudioInput}
            onChange={(e) => handleAudioInputChange(e.target.value)}
            sx={{
              backgroundColor: "rgba(36, 36, 36, 0.6)",
              backdropFilter: "blur(8px)",
              color: Colors.White,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.1)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.2)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.Yellow,
              },
              "& .MuiSvgIcon-root": {
                color: Colors.White,
              },
              borderRadius: "12px",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "rgba(36, 36, 36, 0.95)",
                  backdropFilter: "blur(8px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  marginTop: "8px",
                  "& .MuiMenuItem-root": {
                    color: Colors.White,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(220, 247, 109, 0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(220, 247, 109, 0.25)",
                      },
                    },
                  },
                },
              },
            }}
          >
            {audioInputs.map((device) => (
              <MenuItem
                key={device.deviceId}
                value={device.deviceId}
                sx={{
                  "&.Mui-selected": {
                    color: Colors.Yellow,
                  },
                }}
              >
                {device.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography
          variant="subtitle1"
          sx={{ color: Colors.White, mb: 1, fontWeight: 500 }}
        >
          Camera
        </Typography>
        <FormControl fullWidth>
          <Select
            value={selectedVideoInput}
            onChange={(e) => handleVideoInputChange(e.target.value)}
            sx={{
              backgroundColor: "rgba(36, 36, 36, 0.6)",
              backdropFilter: "blur(8px)",
              color: Colors.White,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.1)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.2)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.Yellow,
              },
              "& .MuiSvgIcon-root": {
                color: Colors.White,
              },
              borderRadius: "12px",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "rgba(36, 36, 36, 0.95)",
                  backdropFilter: "blur(8px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  marginTop: "8px",
                  "& .MuiMenuItem-root": {
                    color: Colors.White,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(220, 247, 109, 0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(220, 247, 109, 0.25)",
                      },
                    },
                  },
                },
              },
            }}
          >
            {videoInputs.map((device) => (
              <MenuItem
                key={device.deviceId}
                value={device.deviceId}
                sx={{
                  "&.Mui-selected": {
                    color: Colors.Yellow,
                  },
                }}
              >
                {device.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </DialogContent>
  </Dialog>
)

const ShareDialog = ({ open, onClose }) => {
  const [copied, setCopied] = useState(false)
  const callLink = window.location.href

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(callLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy link:", err)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          borderRadius: "24px",
          minWidth: { xs: "90vw", sm: "400px" },
          maxWidth: "500px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: Colors.White,
          pb: 1,
        }}
      >
        Share Call Link
        <IconButton
          onClick={onClose}
          sx={{
            color: Colors.White,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: Colors.White, mb: 2, fontWeight: 500 }}
          >
            Copy this link to share the call with others
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
            <TextField
              value={callLink}
              fullWidth
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: "rgba(36, 36, 36, 0.6)",
                  backdropFilter: "blur(8px)",
                  color: Colors.White,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: Colors.Yellow,
                  },
                  borderRadius: "12px",
                  fontSize: "14px",
                },
              }}
            />
            <Button
              onClick={handleCopyLink}
              variant="contained"
              sx={{
                backgroundColor: copied
                  ? Colors.Yellow
                  : "rgba(220, 247, 109, 0.15)",
                color: copied ? "black" : Colors.Yellow,
                borderRadius: "12px",
                minWidth: "auto",
                px: 2,
                "&:hover": {
                  backgroundColor: copied
                    ? Colors.Yellow
                    : "rgba(220, 247, 109, 0.25)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <Copy size={20} style={{ marginRight: copied ? "8px" : "0" }} />
              {copied && "Copied!"}
            </Button>
          </Box>

          {copied && (
            <Typography
              variant="caption"
              sx={{
                color: Colors.Yellow,
                mt: 1,
                display: "block",
                fontWeight: 500,
              }}
            >
              Link copied to clipboard!
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export function CustomControlsBar({
  showChat,
  toggleChat,
  disconnect,
  audioState,
  videoState,
}: CustomControlsBarProps) {
  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(audioState)
  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(videoState)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [audioInputs, setAudioInputs] = useState<DeviceOption[]>([])
  const [videoInputs, setVideoInputs] = useState<DeviceOption[]>([])
  const context = useAppContext()
  const { userChoices, saveAudioInputDeviceId, saveVideoInputDeviceId } =
    usePersistentUserChoices({})

  const selectedAudioInput = context.audioDevice || ""
  const selectedVideoInput = context.videoDevice || ""

  const { localParticipant } = useLocalParticipant()
  const room = useRoomContext()

  const dispatch = useAppDispatchContext()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const availableAudioInputs = useMediaDevices({ kind: "audioinput" })
  const availableVideoInputs = useMediaDevices({ kind: "videoinput" })

  const [hasAppliedInitialDevices, setHasAppliedInitialDevices] =
    useState(false)

  React.useEffect(() => {
    if (availableAudioInputs.length > 0) {
      setAudioInputs(
        availableAudioInputs.map((device) => ({
          label: device.label,
          deviceId: device.deviceId,
        })),
      )
      if (!selectedAudioInput) {
        const audioInput = availableAudioInputs[0].deviceId
        dispatch({ type: StateAction.setAudioDevice, payload: audioInput })
        saveAudioInputDeviceId(audioInput)
      }
    }

    if (availableVideoInputs.length > 0) {
      setVideoInputs(
        availableVideoInputs.map((device) => ({
          label: device.label,
          deviceId: device.deviceId,
        })),
      )
      if (!selectedVideoInput) {
        const videoInput = availableVideoInputs[0].deviceId
        dispatch({ type: StateAction.setVideoDevice, payload: videoInput })
        saveVideoInputDeviceId(videoInput)
      }
    }
  }, [
    availableAudioInputs,
    availableVideoInputs,
    selectedAudioInput,
    selectedVideoInput,
    dispatch,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  ])

  React.useEffect(() => {
    if (
      localParticipant &&
      room &&
      selectedAudioInput &&
      !hasAppliedInitialDevices
    ) {
      const audioTrack = localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )?.track
      if (audioTrack) {
        audioTrack
          .setDeviceId({ exact: selectedAudioInput })
          .then(() => {
            setHasAppliedInitialDevices(true)
          })
          .catch((e) => {
            console.error("Failed to apply initial audio device:", e)
            setHasAppliedInitialDevices(true)
          })
      }
    }
  }, [localParticipant, room, selectedAudioInput, hasAppliedInitialDevices])

  const toggleMicrophone = async () => {
    if (localParticipant) {
      try {
        const enabled = !isMicEnabled
        await localParticipant.setMicrophoneEnabled(enabled)
        setIsMicEnabled(enabled)
      } catch (e) {
        console.error("Failed to toggle microphone:", e)
      }
    }
  }

  const toggleCamera = async () => {
    if (localParticipant) {
      try {
        const enabled = !isCameraEnabled
        await localParticipant.setCameraEnabled(enabled)
        setIsCameraEnabled(enabled)
      } catch (e) {
        console.error("Failed to toggle camera:", e)
      }
    }
  }

  const handleSettingsClick = () => {
    setSettingsOpen(true)
  }

  const handleSettingsClose = () => {
    setSettingsOpen(false)
  }

  const handleShareClick = () => {
    setShareOpen(true)
  }

  const handleShareClose = () => {
    setShareOpen(false)
  }

  const handleAudioInputChange = async (deviceId: string) => {
    if (localParticipant && room) {
      try {
        dispatch({ type: StateAction.setAudioDevice, payload: deviceId })
        saveAudioInputDeviceId(deviceId)

        const audioTrack = localParticipant.getTrackPublication(
          Track.Source.Microphone,
        )?.track
        if (audioTrack) {
          await audioTrack.setDeviceId({ exact: deviceId })
        } else {
          await room.switchActiveDevice("audioinput", deviceId, true)
        }
      } catch (e) {
        console.error("Failed to switch audio device:", e)
      }
    }
  }

  const handleVideoInputChange = async (deviceId: string) => {
    if (localParticipant && room) {
      try {
        dispatch({ type: StateAction.setVideoDevice, payload: deviceId })
        saveVideoInputDeviceId(deviceId)

        const videoTrack = localParticipant.getTrackPublication(
          Track.Source.Camera,
        )?.track
        if (videoTrack) {
          await localParticipant.setCameraEnabled(false)
          await videoTrack.setDeviceId({ exact: deviceId })
          await localParticipant.setCameraEnabled(true)
          setIsCameraEnabled(true)
        } else {
          await room.switchActiveDevice("videoinput", deviceId, true)
        }
      } catch (e) {
        console.error("Failed to switch video device:", e)
      }
    }
  }

  return (
    <React.Fragment>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent={"space-around"}
        alignItems={"center"}
        gap={2}
        sx={{
          position: "fixed",
          bottom: isMobile
            ? "2.5rem"
            : "var(--spacing-tokens-size-in-px-spacing-spacing-5xl)",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          padding: "0.75rem",
          borderRadius: "50px",
          zIndex: 1000,
        }}
      >
        <Box
          {...containerProps}
          onClick={toggleCamera}
          data-testid="camera-button"
          sx={{
            ...containerProps.sx,
            backgroundColor: !isCameraEnabled
              ? "black"
              : "rgba(36, 36, 36, 0.6)",
            "&:hover": {
              backgroundColor: !isCameraEnabled
                ? "black"
                : "rgba(36, 36, 36, 0.8)",
            },
          }}
        >
          {isCameraEnabled ? (
            <VideoCamera {...iconProps} color={Colors.White} />
          ) : (
            <VideoCameraSlash {...iconProps} color={Colors.Yellow} />
          )}
        </Box>
        <Box
          {...containerProps}
          onClick={toggleMicrophone}
          data-testid="microphone-button"
          sx={{
            ...containerProps.sx,
            backgroundColor: !isMicEnabled ? "black" : "rgba(36, 36, 36, 0.6)",
            "&:hover": {
              backgroundColor: !isMicEnabled
                ? "black"
                : "rgba(36, 36, 36, 0.8)",
            },
          }}
        >
          {isMicEnabled ? (
            <Microphone {...iconProps} color={Colors.White} />
          ) : (
            <MicrophoneSlash {...iconProps} color={Colors.Yellow} />
          )}
        </Box>
        <Box
          {...containerProps}
          onClick={toggleChat}
          data-testid="chat-button"
          sx={{
            ...containerProps.sx,
            backgroundColor: showChat ? "black" : "rgba(36, 36, 36, 0.6)",
            "&:hover": {
              backgroundColor: showChat ? "black" : "rgba(36, 36, 36, 0.8)",
            },
          }}
        >
          <ChatCircleText
            {...iconProps}
            color={showChat ? Colors.Yellow : Colors.White}
          />
        </Box>
        {/* <Box
          {...containerProps}
          onClick={handleShareClick}
          data-testid="share-button"
        >
          <Export {...iconProps} color={Colors.White} />
        </Box> */}
        <Box {...containerProps} onClick={handleSettingsClick}>
          <Gear {...iconProps} color={Colors.White} />
        </Box>
        <Box
          {...containerProps}
          onClick={disconnect}
          data-testid="end-call-button"
        >
          <PhoneSlash {...iconProps} color="red" />
        </Box>
      </Box>

      {/* <ShareDialog open={shareOpen} onClose={handleShareClose} /> */}

      <DeviceSelectionDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioInput={selectedAudioInput}
        selectedVideoInput={selectedVideoInput}
        handleAudioInputChange={handleAudioInputChange}
        handleVideoInputChange={handleVideoInputChange}
      />
    </React.Fragment>
  )
}
